import dayjs from 'dayjs';

export const dateIsBetween = ({
    date,
    from,
    to,
    equals = false,
}: {
    date: dayjs.Dayjs;
    from?: dayjs.Dayjs;
    to?: dayjs.Dayjs;
    equals?: boolean;
}): boolean => {
    if (equals) {
        return (
            ((date.isAfter(from) || !from) && (date.isBefore(to) || !to)) ||
            (!!from && date.isSame(from)) ||
            (!!to && date.isSame(to))
        );
    }
    return (date.isAfter(from) || !from) && (date.isBefore(to) || !to);
};

/**
 * Parse a yearless date string (MM/DD format) to various date formats
 */
export const parseYearlessDate = {
    /**
     * Check if a date falls within a yearless date range, handling cross-year zones
     */
    isInRange: (inputDate: string | Date | dayjs.Dayjs, minDate: string, maxDate: string): boolean => {
        if (!minDate || !maxDate) return true;

        // Convert input to MM/DD format
        let inputMonthDay: string;
        if (typeof inputDate === 'string') {
            // If it's already MM/DD format, use as is; otherwise parse as full date
            if (inputDate.includes('/') && inputDate.length === 5) {
                inputMonthDay = inputDate;
            } else {
                inputMonthDay = dayjs(inputDate).format('MM/DD');
            }
        } else if (inputDate instanceof Date) {
            inputMonthDay = dayjs(inputDate).format('MM/DD');
        } else {
            inputMonthDay = inputDate.format('MM/DD');
        }

        // Handle zones that span across years (e.g., "11/15" to "01/31")
        if (minDate > maxDate) {
            // Cross-year zone: date is valid if it's >= minDate OR <= maxDate
            return inputMonthDay >= minDate || inputMonthDay <= maxDate;
        }
        // Same-year zone: date is valid if it's between minDate and maxDate
        return inputMonthDay >= minDate && inputMonthDay <= maxDate;
    },
};

/**
 * Assign years to yearless date ranges based on current date
 * If today > maxDate, then add one year to both dates
 * If defaultPlantingDateStr is provided, it will be assigned to the correct year within the range
 *
 * Example: min_date: 11/01 max_date: 02/01 today: 11/05/24 => min_date = 11/01/24 max_date = 02/01/25
 * Example: min_date: 11/01 max_date: 02/01 today: 01/25/25 => min_date = 11/01/24 max_date = 02/01/25
 * Example: min_date: 11/01 max_date: 02/01 today: 02/05/25 => min_date = 11/01/25 max_date = 02/01/26
 * Example with default planting date: min_date: 11/01 max_date: 02/01 default: 12/15 today: 02/05/25
 *          => min_date = 11/01/25 max_date = 02/01/26 defaultPlantingDate = 12/15/25
 */
export const assignYearsToDateRange = (
    minDateStr: string,
    maxDateStr: string,
    defaultPlantingDateStr: string | null = null,
    referenceDate: Date = new Date()
): { minDate: string; maxDate: string; defaultPlantingDate?: string } | null => {
    if (!minDateStr || !maxDateStr) return null;

    const [minMonth, minDay] = minDateStr.split('/').map(Number);
    const [maxMonth, maxDay] = maxDateStr.split('/').map(Number);

    if (!minMonth || !minDay || !maxMonth || !maxDay) return null;

    const currentYear = referenceDate.getFullYear();
    const currentDate = dayjs(referenceDate);

    // Create max date in current year to compare with today
    const maxDateCurrentYear = dayjs()
        .year(currentYear)
        .month(maxMonth - 1)
        .date(maxDay);

    // Check if this is a cross-year range
    const isCrossYear = minMonth > maxMonth || (minMonth === maxMonth && minDay > maxDay);

    // Helper function to assign default planting date to correct year
    const assignDefaultPlantingDate = (minYear: number, maxYear: number) => {
        if (!defaultPlantingDateStr || !defaultPlantingDateStr.includes('/')) return undefined;

        const [defaultMonth, defaultDay] = defaultPlantingDateStr.split('/').map(Number);
        if (!defaultMonth || !defaultDay) return undefined;

        if (isCrossYear) {
            // Cross-year range - determine which year the default planting date should be in
            if (defaultMonth >= minMonth) {
                // Default planting date is in the "min" part (later months)
                return dayjs()
                    .year(minYear)
                    .month(defaultMonth - 1)
                    .date(defaultDay)
                    .format('YYYY-MM-DD');
            }
            // Default planting date is in the "max" part (earlier months)
            return dayjs()
                .year(maxYear)
                .month(defaultMonth - 1)
                .date(defaultDay)
                .format('YYYY-MM-DD');
        }
        // Same year range - use the same year as min/max
        return dayjs()
            .year(minYear)
            .month(defaultMonth - 1)
            .date(defaultDay)
            .format('YYYY-MM-DD');
    };

    if (isCrossYear) {
        // Cross-year range (e.g., 11/01 to 02/01)
        // If today > maxDate, use next year transition
        if (currentDate.isAfter(maxDateCurrentYear)) {
            const minYear = currentYear;
            const maxYear = currentYear + 1;
            return {
                minDate: dayjs()
                    .year(minYear)
                    .month(minMonth - 1)
                    .date(minDay)
                    .format('YYYY-MM-DD'),
                maxDate: dayjs()
                    .year(maxYear)
                    .month(maxMonth - 1)
                    .date(maxDay)
                    .format('YYYY-MM-DD'),
                defaultPlantingDate: assignDefaultPlantingDate(minYear, maxYear),
            };
        }
        // Use previous year transition
        const minYear = currentYear - 1;
        const maxYear = currentYear;
        return {
            minDate: dayjs()
                .year(minYear)
                .month(minMonth - 1)
                .date(minDay)
                .format('YYYY-MM-DD'),
            maxDate: dayjs()
                .year(maxYear)
                .month(maxMonth - 1)
                .date(maxDay)
                .format('YYYY-MM-DD'),
            defaultPlantingDate: assignDefaultPlantingDate(minYear, maxYear),
        };
    }

    // Same year range (e.g., 08/01 to 11/30)
    // If today > maxDate, use next year
    if (currentDate.isAfter(maxDateCurrentYear)) {
        const year = currentYear + 1;
        return {
            minDate: dayjs()
                .year(year)
                .month(minMonth - 1)
                .date(minDay)
                .format('YYYY-MM-DD'),
            maxDate: dayjs()
                .year(year)
                .month(maxMonth - 1)
                .date(maxDay)
                .format('YYYY-MM-DD'),
            defaultPlantingDate: assignDefaultPlantingDate(year, year),
        };
    }
    // Use current year
    const year = currentYear;
    return {
        minDate: dayjs()
            .year(year)
            .month(minMonth - 1)
            .date(minDay)
            .format('YYYY-MM-DD'),
        maxDate: dayjs()
            .year(year)
            .month(maxMonth - 1)
            .date(maxDay)
            .format('YYYY-MM-DD'),
        defaultPlantingDate: assignDefaultPlantingDate(year, year),
    };
};

export const getMinMaxDate = (dateRanges: [][]) => {
    if (!dateRanges || !Array.isArray(dateRanges)) return;
    const flattenedDates = dateRanges.flat();
    const allDates = flattenedDates.map((date) => dayjs(date)).filter((date) => date.isValid());
    if (allDates.length === 0) return;
    const minDate = dayjs.min(allDates);
    const maxDate = dayjs.max(allDates);
    return { minDate, maxDate };
};

/**
 * Calculate current campaign year from fallow zone using only min_date
 * Uses parseYearlessDate to determine the campaign year based on the fallow zone's min_date
 *
 * Logic (following backend calculate_fallow_rule_campaign):
 * - Uses parseYearlessDate to convert reference date to MM/DD format
 * - Compares reference date (MM/DD) with fallow zone min_date to determine campaign year
 * - If reference_date (MM/DD) >= fallow_zone.min_date => reference_date.year
 * - Else => reference_date.year - 1
 */
export const getCurrentCampaignYearFromFallowZone = (
    fallowZoneMinDate: string | null | undefined,
    referenceDate: Date = new Date()
): number | null => {
    if (!fallowZoneMinDate) {
        return null;
    }

    // Use parseYearlessDate logic to convert reference date to MM/DD format
    const referenceDateMMDD = dayjs(referenceDate).format('MM/DD');

    // Compare yearless dates directly (MM/DD format comparison)
    // If reference_date >= min_date => current year, else => current year - 1
    if (referenceDateMMDD >= fallowZoneMinDate) {
        return referenceDate.getFullYear();
    }

    return referenceDate.getFullYear() - 1;
};

/**
 * Anchors a yearless date string (MM/DD) to a specific year.
 *
 * Built from an explicit year/month/day so it is unaffected by the current day-of-month
 * (chaining `.month().date()` on `dayjs()` can overflow for short months). Returns null
 * for malformed input.
 */
export const getYearlessDate = (monthDay: string, year: number): dayjs.Dayjs | null => {
    const [month, day] = (monthDay ?? '').split('/').map(Number);
    if (!month || !day) return null;
    return dayjs(new Date(year, month - 1, day));
};

/**
 * Creates a shouldDisableDate function for MuiDatePicker that validates dates against
 * multiple date ranges, including previous and next year variations.
 *
 * @param validDates - Array of date range tuples [minDate, maxDate] as strings
 * @param yearsToTest - Array of year offsets to test (e.g., [-1, 0, 1] for previous, current, and next year)
 * @returns A function that returns true if the date should be disabled, false if it's valid
 *
 * @example
 * const shouldDisableDate = createPlantingDateValidator([['2024-01-01', '2024-03-31'], ['2024-10-01', '2024-12-31']]);
 * <MuiDatePicker shouldDisableDate={shouldDisableDate} />
 */
export const createPlantingDateValidator = (
    validDates: [string, string][] | undefined,
    yearsToTest: number[] = [0]
): ((date: dayjs.Dayjs) => boolean) => {
    return (date: dayjs.Dayjs) => {
        if (!validDates || validDates.length === 0) return false;

        // Check if the date falls within any valid range (including year variations)
        for (const [minDate, maxDate] of validDates) {
            const isInRange = yearsToTest.some((yearOffset) => {
                const adjustedMinDate = dayjs(minDate).add(yearOffset, 'year');
                const adjustedMaxDate = dayjs(maxDate).add(yearOffset, 'year');
                return dateIsBetween({
                    date,
                    from: adjustedMinDate,
                    to: adjustedMaxDate,
                    equals: true,
                });
            });

            if (isInRange) {
                return false; // Date is valid, don't disable
            }
        }

        return true; // Date is not in any valid range, disable it
    };
};
